import { MenuItem } from "prosemirror-menu";
import { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { imageIconSpec } from "../icons";
import { handleImageUploads } from "../plugins/image-drop";
import { ALLOWED_IMAGE_MIME_TYPES, isFormulaSelected, isImageSelected } from "../utils";

import { t } from "i18n/i18n";
import { rollbarAndLogError } from "utils/logger";

/**
 * Creates a toolbar MenuItem for inserting images.
 *
 * When clicked, opens the browser's file picker, uploads the selected image,
 * and inserts it at the current cursor position.
 */
export function imageItem(): MenuItem {
  return new MenuItem({
    title: t("worksheet.rich_text_editor.insert_images"),
    icon: imageIconSpec,
    enable(state: EditorState) {
      return !isFormulaSelected(state) && !isImageSelected(state);
    },
    run: (state: EditorState, dispatch, view: EditorView) => {
      const wrapperElement = view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement | null;
      if (!wrapperElement) {
        rollbarAndLogError("[ProseMirrorEditor] Wrapper element not found for image upload");
        return;
      }

      // Create a hidden file input element
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = Object.values(ALLOWED_IMAGE_MIME_TYPES).join(",");
      input.style.display = "none";

      const cleanup = () => {
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      const handleFileSelect = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;
        if (!files || files.length === 0) {
          cleanup();
          return;
        }

        return handleImageUploads(view, ...files).finally(cleanup);
      };

      input.addEventListener("change", handleFileSelect, { once: true });

      // Handle window focus to clean up if user cancels (closes file picker)
      const handleFocus = () => {
        // Small delay to allow change event to fire if file was selected
        setTimeout(() => {
          if (input.parentNode && (!input.files || input.files.length === 0)) {
            cleanup();
          }
        }, 100);
      };
      window.addEventListener("focus", handleFocus, { once: true });

      // Append to wrapper temporarily and trigger click
      wrapperElement.appendChild(input);
      input.click();
    },
  });
}
